import { capacityMileageBandLabel } from '../../../utils/mileageBands';
import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import schedulingService from '../../../services/scheduling.service';
import { DriverAvailabilityResponse, DriverAvailabilityDay, DriverStatusBreakdown, TerminalSettings, TrucksBreakdown } from '../../../types/scheduling';

const EMPTY_DRIVER_BREAKDOWN: DriverStatusBreakdown = {
  available: 0,
  accepted: 0,
  accepted_confirmed: 0,
  planned: 0,
  in_route_otr: 0,
  not_working: 0,
  out_of_service: 0,
  unknown: 0,
};

const DRIVER_BREAKDOWN_LABELS: { key: keyof DriverStatusBreakdown; label: string }[] = [
  { key: 'accepted', label: 'Accepted' },
  { key: 'accepted_confirmed', label: 'Accepted Confirmed' },
  { key: 'available', label: 'Available' },
  { key: 'in_route_otr', label: 'In Route OTR' },
  { key: 'not_working', label: 'Not Working' },
  { key: 'out_of_service', label: 'Out of Service' },
  { key: 'planned', label: 'Planned' },
  { key: 'unknown', label: 'Unknown' },
];

function sumBreakdowns(a: DriverStatusBreakdown, b: DriverStatusBreakdown): DriverStatusBreakdown {
  return {
    available: a.available + b.available,
    accepted: a.accepted + b.accepted,
    accepted_confirmed: a.accepted_confirmed + b.accepted_confirmed,
    planned: a.planned + b.planned,
    in_route_otr: a.in_route_otr + b.in_route_otr,
    not_working: a.not_working + b.not_working,
    out_of_service: a.out_of_service + b.out_of_service,
    unknown: a.unknown + b.unknown,
  };
}

type PreferenceType = 'LOCAL' | 'OTR';

interface DriverAvailabilityWidgetProps {
  terminal?: string;
}

// Format date as MM/DD
const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr + 'T00:00:00');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}/${day}`;
};

const VIEWPORT_GUTTER = 8;

// Left edge that centers a tooltip of `width` on `center` without leaving the viewport.
function clampTooltipLeft(center: number, width: number, viewportWidth: number): number {
  const maxLeft = viewportWidth - VIEWPORT_GUTTER - width;
  return Math.max(VIEWPORT_GUTTER, Math.min(center - width / 2, maxLeft));
}

// Fixed-position style for a portaled tooltip centered below its anchor cell. The tooltip is
// measured after mount so the last day columns can't push it off the right edge of the page.
function useClampedTooltipStyle(anchorRect: DOMRect) {
  const ref = useRef<HTMLDivElement>(null);
  const [left, setLeft] = useState<number | null>(null);

  useLayoutEffect(() => {
    const width = ref.current?.getBoundingClientRect().width ?? 0;
    setLeft(clampTooltipLeft(anchorRect.left + anchorRect.width / 2, width, window.innerWidth));
  }, [anchorRect]);

  const style: React.CSSProperties = {
    position: 'fixed',
    top: anchorRect.bottom + 4,
    left: left ?? 0,
    visibility: left === null ? 'hidden' : undefined,
    zIndex: 9999,
  };
  return { ref, style };
}

// Breakdown Tooltip Component - portaled to body
function BreakdownTooltip({
  date,
  breakdown,
  anchorRect,
  hidePrepulls
}: {
  date: string;
  breakdown: TrucksBreakdown;
  anchorRect: DOMRect;
  hidePrepulls?: boolean;
}) {
  const { ref, style } = useClampedTooltipStyle(anchorRect);

  return createPortal(
    <div ref={ref} style={style} className="bg-slate-700 text-white rounded shadow-lg text-xs whitespace-nowrap">
      <table className="border-collapse">
        <thead>
          <tr className="border-b border-slate-500">
            <th className="px-3 py-1.5 text-left font-semibold">{formatDate(date)}</th>
            <th className="px-3 py-1.5 text-center font-semibold">PREPULLS</th>
            <th className="px-3 py-1.5 text-center font-semibold">DELIVERIES</th>
            <th className="px-3 py-1.5 text-center font-semibold">RETURNS</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-slate-600">
            <td className="px-3 py-1 text-slate-300">Planning</td>
            <td className={`px-3 py-1 text-center ${hidePrepulls ? 'text-slate-500 line-through' : ''}`}>{breakdown.prepulls.planning}</td>
            <td className="px-3 py-1 text-center">{breakdown.deliveries.planning}</td>
            <td className="px-3 py-1 text-center">{breakdown.returns?.planning ?? 0}</td>
          </tr>
          <tr className="border-b border-slate-600">
            <td className="px-3 py-1 text-slate-300">Pending</td>
            <td className={`px-3 py-1 text-center ${hidePrepulls ? 'text-slate-500 line-through' : ''}`}>{breakdown.prepulls.pending}</td>
            <td className="px-3 py-1 text-center">{breakdown.deliveries.pending}</td>
            <td className="px-3 py-1 text-center">{breakdown.returns?.pending ?? 0}</td>
          </tr>
          <tr className="border-b border-slate-600">
            <td className="px-3 py-1 text-slate-300">Finalized</td>
            <td className={`px-3 py-1 text-center ${hidePrepulls ? 'text-slate-500 line-through' : ''}`}>{breakdown.prepulls.finalized}</td>
            <td className="px-3 py-1 text-center">{breakdown.deliveries.finalized}</td>
            <td className="px-3 py-1 text-center">{breakdown.returns?.finalized ?? 0}</td>
          </tr>
          <tr>
            <td className="px-3 py-1.5 font-semibold">Total</td>
            <td className={`px-3 py-1.5 text-center font-semibold ${hidePrepulls ? 'text-slate-500 line-through' : ''}`}>{breakdown.prepulls.total}</td>
            <td className="px-3 py-1.5 text-center font-semibold">{breakdown.deliveries.total}</td>
            <td className="px-3 py-1 text-center">{breakdown.returns?.total ?? 0}</td>
          </tr>
        </tbody>
      </table>
      {breakdown.delivery_mileage_bands && (
        <table className="border-collapse w-full border-t border-slate-500" aria-label="Deliveries by mileage band">
          <caption className="px-3 py-1.5 text-left text-slate-300">Deliveries by one-way mileage</caption>
          <thead>
            <tr className="border-b border-slate-500">
              {['Mileage band', 'Planning', 'Pending', 'Finalized', 'Total'].map(label => (
                <th key={label} className="px-3 py-1 text-left font-semibold">{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(breakdown.delivery_mileage_bands).map(([band, counts]) => (
              <tr key={band} className="border-b border-slate-600 last:border-b-0">
                <td className="px-3 py-1 text-slate-300">{capacityMileageBandLabel(band)}</td>
                <td className="px-3 py-1 text-center">{counts.planning}</td>
                <td className="px-3 py-1 text-center">{counts.pending}</td>
                <td className="px-3 py-1 text-center">{counts.finalized}</td>
                <td className="px-3 py-1 text-center font-semibold">{counts.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>,
    document.body
  );
}

function DriverBreakdownTooltip({
  date,
  breakdown,
  anchorRect,
}: {
  date: string;
  breakdown: DriverStatusBreakdown;
  anchorRect: DOMRect;
}) {
  const { ref, style } = useClampedTooltipStyle(anchorRect);

  return createPortal(
    <div ref={ref} style={style} className="bg-slate-700 text-white rounded shadow-lg text-xs whitespace-nowrap">
      <table className="border-collapse">
        <thead>
          <tr className="border-b border-slate-500">
            <th colSpan={2} className="px-3 py-1.5 text-left font-semibold">{formatDate(date)}</th>
          </tr>
        </thead>
        <tbody>
          {DRIVER_BREAKDOWN_LABELS.map(({ key, label }) => (
            <tr key={key} className="border-b border-slate-600 last:border-b-0">
              <td className="px-3 py-1 text-slate-300">{label}</td>
              <td className="px-3 py-1 text-right font-semibold">{breakdown[key]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>,
    document.body
  );
}

export default function DriverAvailabilityWidget({ terminal }: DriverAvailabilityWidgetProps) {
  const [preference, setPreference] = useState<PreferenceType>('LOCAL');
  const [data, setData] = useState<DriverAvailabilityResponse | null>(null);
  const [terminalSettings, setTerminalSettings] = useState<TerminalSettings[]>([]);
  const [loading, setLoading] = useState(true);
  const [returnRevision, setReturnRevision] = useState(0);
  useEffect(() => {
    const refresh = () => setReturnRevision(value => value + 1);
    window.addEventListener('scheduling:returns-updated', refresh);
    return () => window.removeEventListener('scheduling:returns-updated', refresh);
  }, []);
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);
  const [hoveredRow, setHoveredRow] = useState<'req' | 'avail' | null>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      schedulingService.getDriverAvailability(terminal ? [terminal] : undefined),
      schedulingService.getTerminalSettings(),
    ])
      .then(([availData, settingsData]) => {
        setData(availData);
        setTerminalSettings(settingsData);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [terminal, returnRevision]);

  // Show combined TOTAL when: no terminal selected OR terminal has Combined truck pool type
  const isCombined = useMemo(() => {
    // No terminal selected = show combined totals for all regions
    if (!terminal) return true;
    // Terminal selected = check if it's Combined type
    const settings = terminalSettings.find(s => s.terminal.toUpperCase() === terminal.toUpperCase());
    return settings?.truck_pool_type === 'Combined';
  }, [terminal, terminalSettings]);

  // Get the days to display based on pool type
  const days: DriverAvailabilityDay[] = useMemo(() => {
    if (!data) return [];

    if (isCombined) {
      // Combine LOCAL + OTR counts; prepulls are already merged by the backend.
      return data.local.map((localDay, idx) => {
        const otrDay = data.otr[idx];
        return {
          date: localDay.date,
          working_count: localDay.working_count + (otrDay?.working_count || 0),
          trucks_required: localDay.trucks_required + (otrDay?.trucks_required || 0),
          breakdown: localDay.breakdown ? {
            ...localDay.breakdown,
            deliveries: {
              planning: localDay.breakdown.deliveries.planning + (otrDay?.breakdown?.deliveries.planning ?? 0),
              pending: localDay.breakdown.deliveries.pending + (otrDay?.breakdown?.deliveries.pending ?? 0),
              finalized: localDay.breakdown.deliveries.finalized + (otrDay?.breakdown?.deliveries.finalized ?? 0),
              total: localDay.breakdown.deliveries.total + (otrDay?.breakdown?.deliveries.total ?? 0),
            },
            delivery_mileage_bands: Object.fromEntries(
              [...new Set([
                ...Object.keys(localDay.breakdown.delivery_mileage_bands ?? {}),
                ...Object.keys(otrDay?.breakdown?.delivery_mileage_bands ?? {}),
              ])].map(band => {
                const local = localDay.breakdown?.delivery_mileage_bands?.[band];
                const otr = otrDay?.breakdown?.delivery_mileage_bands?.[band];
                return [band, {
                  planning: (local?.planning ?? 0) + (otr?.planning ?? 0),
                  pending: (local?.pending ?? 0) + (otr?.pending ?? 0),
                  finalized: (local?.finalized ?? 0) + (otr?.finalized ?? 0),
                  total: (local?.total ?? 0) + (otr?.total ?? 0),
                }];
              }),
            ),
            returns: {
              planning: (localDay.breakdown.returns?.planning ?? 0) + (otrDay?.breakdown?.returns?.planning ?? 0),
              pending: (localDay.breakdown.returns?.pending ?? 0) + (otrDay?.breakdown?.returns?.pending ?? 0),
              finalized: (localDay.breakdown.returns?.finalized ?? 0) + (otrDay?.breakdown?.returns?.finalized ?? 0),
              total: (localDay.breakdown.returns?.total ?? 0) + (otrDay?.breakdown?.returns?.total ?? 0),
            },
          } : undefined,
          driver_breakdown: otrDay
            ? sumBreakdowns(localDay.driver_breakdown, otrDay.driver_breakdown)
            : localDay.driver_breakdown,
        };
      });
    }

    return preference === 'LOCAL' ? data.local : data.otr;
  }, [data, isCombined, preference]);

  // Hide prepulls in tooltip when the current toggle doesn't match the terminal's prepull_type
  const hidePrepulls = useMemo(() => {
    if (isCombined || !terminal) return false;
    const settings = terminalSettings.find(s => s.terminal.toUpperCase() === terminal.toUpperCase());
    if (!settings?.prepull_type) return false;
    return settings.prepull_type.toUpperCase() !== preference;
  }, [isCombined, terminal, terminalSettings, preference]);

  const handleMouseEnter = (e: React.MouseEvent<HTMLTableCellElement>, date: string, row: 'req' | 'avail') => {
    const rect = e.currentTarget.getBoundingClientRect();
    setAnchorRect(rect);
    setHoveredDate(date);
    setHoveredRow(row);
  };

  const handleMouseLeave = () => {
    setHoveredDate(null);
    setHoveredRow(null);
    setAnchorRect(null);
  };

  if (!terminal) return null;

  if (loading) {
    return <div className="text-xs text-gray-500">Loading...</div>;
  }

  const hoveredDay = days.find(d => d.date === hoveredDate);

  return (
    <div className="flex-shrink-0 border border-gray-300 dark:border-gray-600 rounded overflow-hidden">
      <table className="text-xs border-collapse">
        <thead>
          <tr className="bg-slate-700 text-white">
            {/* Toggle buttons or Total label */}
            <th className="px-2 py-1.5 text-left">
              {isCombined ? (
                <span className="px-2 py-0.5 text-xs font-semibold bg-white text-slate-700 rounded">
                  TOTAL
                </span>
              ) : (
                <div className="flex">
                  <button
                    type="button"
                    onClick={() => setPreference('LOCAL')}
                    className={`px-2 py-0.5 text-xs font-semibold rounded-l transition-colors ${
                      preference === 'LOCAL'
                        ? 'bg-white text-slate-700'
                        : 'bg-slate-600 text-white hover:bg-slate-500'
                    }`}
                  >
                    LOCAL
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreference('OTR')}
                    className={`px-2 py-0.5 text-xs font-semibold rounded-r transition-colors ${
                      preference === 'OTR'
                        ? 'bg-white text-slate-700'
                        : 'bg-slate-600 text-white hover:bg-slate-500'
                    }`}
                  >
                    OTR
                  </button>
                </div>
              )}
            </th>
            {/* Date headers */}
            {days.map((day) => (
              <th key={day.date} className="px-3 py-1.5 text-center font-semibold">
                {formatDate(day.date)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-panel">
          {/* Trucks Required row */}
          <tr className="border-b border-gray-200 dark:border-gray-700">
            <td className="px-2 py-1 text-gray-600 dark:text-gray-400 whitespace-nowrap">Trucks Req</td>
            {days.map((day) => {
              const isOverCapacity = day.trucks_required > day.working_count;
              const hasBreakdown = day.breakdown != null;
              return (
                <td
                  key={`req-${day.date}`}
                  className={`px-3 py-1 text-center font-semibold ${
                    isOverCapacity ? 'text-red-600' : 'text-heading'
                  } ${hasBreakdown ? 'cursor-pointer' : ''}`}
                  onMouseEnter={(e) => hasBreakdown && handleMouseEnter(e, day.date, 'req')}
                  onMouseLeave={handleMouseLeave}
                >
                  {day.trucks_required}
                </td>
              );
            })}
          </tr>
          {/* Trucks Available row */}
          <tr>
            <td className="px-2 py-1 text-gray-600 dark:text-gray-400 whitespace-nowrap">Trucks Avail</td>
            {days.map((day) => (
              <td
                key={`avail-${day.date}`}
                className="px-3 py-1 text-center font-semibold text-heading cursor-pointer"
                onMouseEnter={(e) => handleMouseEnter(e, day.date, 'avail')}
                onMouseLeave={handleMouseLeave}
              >
                {day.working_count}
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      {/* Portaled tooltip — Trucks Req shows prepull/delivery breakdown */}
      {hoveredDate && hoveredRow === 'req' && hoveredDay?.breakdown && anchorRect && (
        <BreakdownTooltip
          date={hoveredDate}
          breakdown={hoveredDay.breakdown}
          anchorRect={anchorRect}
          hidePrepulls={hidePrepulls}
        />
      )}
      {/* Portaled tooltip — Trucks Avail shows driver-status breakdown */}
      {hoveredDate && hoveredRow === 'avail' && hoveredDay && anchorRect && (
        <DriverBreakdownTooltip
          date={hoveredDate}
          breakdown={hoveredDay.driver_breakdown ?? EMPTY_DRIVER_BREAKDOWN}
          anchorRect={anchorRect}
        />
      )}
    </div>
  );
}
